import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { searchAPI } from '@/api';
import MatchCard from '@/components/match/MatchCard';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Search, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localQuery, setLocalQuery] = useState(query);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    if (!query || query.length < 2) return;
    setLoading(true);
    searchAPI(query).then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (localQuery.length >= 2) {
      setSearchParams({ q: localQuery });
    }
  };

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="search-results-page">
      <SEOHead title={`${t("searchPage.title", "Search")}: ${query}`} description={`Search results for "${query}" - Find matches, teams, leagues and bookmakers.`} />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">{t('nav.search', 'Search')}</span>
      </div>

      <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Search className="w-7 h-7 inline mr-2 text-slate-400" />
        {t('searchPage.title', 'Search Results')}
      </h1>

      <form onSubmit={handleSearch} className="mb-6 max-w-lg">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="search-page-input"
            value={localQuery}
            onChange={e => setLocalQuery(e.target.value)}
            placeholder={t('searchPage.placeholder', 'Search teams, leagues, bookmakers...')}
            className="pl-9 bg-slate-900 border-slate-700 text-slate-200"
          />
        </div>
      </form>

      {query && <p className="text-sm text-slate-500 mb-4">{t('searchPage.resultsFor', 'Results for:')} <span className="text-slate-300">"{query}"</span></p>}

      {loading ? <LoadingPage /> : data && (
        <div className="space-y-8">
          {data.matches?.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('searchPage.matches', 'Matches')} <Badge variant="outline" className="ml-2 text-xs border-slate-700 text-slate-500">{data.matches.length}</Badge>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.matches.map(m => <MatchCard key={m.id} match={m} />)}
              </div>
            </section>
          )}

          {data.leagues?.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('searchPage.leagues', 'Leagues')} <Badge variant="outline" className="ml-2 text-xs border-slate-700 text-slate-500">{data.leagues.length}</Badge>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.leagues.map(lg => (
                  <Link key={lg.id} to={`/${lg.sport_id}/${lg.slug}`}
                    className="flex items-center justify-between px-4 py-3 bg-slate-900/50 border border-slate-800 rounded-lg hover:bg-slate-900 transition-all">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 uppercase">{lg.country_code}</span>
                      <span className="text-sm text-slate-300">{lg.name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {data.teams?.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('searchPage.teams', 'Teams')} <Badge variant="outline" className="ml-2 text-xs border-slate-700 text-slate-500">{data.teams.length}</Badge>
              </h2>
              <div className="flex flex-wrap gap-2">
                {data.teams.map(t => (
                  <Badge key={t.id} variant="outline" className="border-slate-700 text-slate-300 px-3 py-1.5">
                    {t.name}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {!data.matches?.length && !data.leagues?.length && !data.teams?.length && (
            <div className="text-center py-12 text-slate-500">{t('searchPage.noResults', 'No results found for')} "{query}"</div>
          )}
        </div>
      )}
    </div>
  );
}
